import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';

import { ProgramsApi } from '@core/api';
import { Program, ProviderServiceKey } from '@shared/entities';
import { ListResponse, Result } from '@shared/models';
import { extractErrorMessage } from '@shared/utils';
import { commandResult, failureResult } from './store-helpers';

type ProgramsState = {
  records: Program[];
  loading: boolean;
  loaded: boolean;
  deletingProgramId: number | null;
  commandInProgress: boolean;
  lastCommandName: string | null;
  lastCommandResult: Result<unknown> | null;
  errorMessage: string | null;
  providerServiceKeys: ProviderServiceKey[];
  loadingProviderServiceKeys: boolean;
  providerServiceKeyProgramId: number | null;
  lastProviderServiceKeyResult: Result<unknown> | null;
};

const initialProgramsState: ProgramsState = {
  records: [],
  loading: false,
  loaded: false,
  deletingProgramId: null,
  commandInProgress: false,
  lastCommandName: null,
  lastCommandResult: null,
  errorMessage: null,
  providerServiceKeys: [],
  loadingProviderServiceKeys: false,
  providerServiceKeyProgramId: null,
  lastProviderServiceKeyResult: null,
};

export const ProgramsStore = signalStore(
  { providedIn: 'root' },
  withState(initialProgramsState),
  withComputed(({ records, loading, deletingProgramId, commandInProgress }) => ({
    response: computed(() => ({ records: records() }) as ListResponse<Program>),
    programCount: computed(() => records().length),
    hasPrograms: computed(() => records().length > 0),
    isBusy: computed(() => loading() || deletingProgramId() !== null || commandInProgress()),
    byId: computed(() => new Map(records().map((program) => [program.programId, program]))),
  })),
  withMethods((store, programsApi = inject(ProgramsApi)) => {
    let loadRequestId = 0;
    let providerServiceKeysRequestId = 0;

    const loadPrograms = (force = false): void => {
      if (!force && store.loaded()) {
        return;
      }

      const requestId = ++loadRequestId;
      patchState(store, { loading: true, errorMessage: null });

      void programsApi
        .loadPrograms(force)
        .then((response) => {
          if (requestId === loadRequestId) {
            patchState(store, { records: response.records, loaded: true });
          }
        })
        .catch((error: unknown) => {
          if (requestId === loadRequestId) {
            patchState(store, { errorMessage: extractErrorMessage(error, 'Unable to load programs.') });
          }
        })
        .finally(() => {
          if (requestId === loadRequestId) {
            patchState(store, { loading: false });
          }
        });
    };

    const listProviderServiceKeys = (programId: number): void => {
      const requestId = ++providerServiceKeysRequestId;
      patchState(store, {
        loadingProviderServiceKeys: true,
        providerServiceKeyProgramId: programId,
        lastProviderServiceKeyResult: null,
        errorMessage: null,
      });

      void programsApi
        .listProviderServiceKeys(programId)
        .then((result) => {
          if (requestId === providerServiceKeysRequestId) {
            patchState(store, {
              providerServiceKeys: result.successful ? (result.data.records ?? []) : [],
              lastProviderServiceKeyResult: commandResult('listProviderServiceKeys', result),
              errorMessage: result.successful ? null : result.message,
            });
          }
        })
        .catch((error: unknown) => {
          if (requestId === providerServiceKeysRequestId) {
            const message = extractErrorMessage(error, 'Unable to load provider service keys.');
            patchState(store, {
              providerServiceKeys: [],
              errorMessage: message,
              lastProviderServiceKeyResult: commandResult('listProviderServiceKeys', failureResult(message)),
            });
          }
        })
        .finally(() => {
          if (requestId === providerServiceKeysRequestId) {
            patchState(store, { loadingProviderServiceKeys: false });
          }
        });
    };

    return {
      load: loadPrograms,
      loadPrograms,
      refresh(): void {
        loadPrograms(true);
      },
      create(program: Program): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'create',
          lastCommandResult: null,
          errorMessage: null,
        });

        void programsApi
          .create(program)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('create', result) });
            if (result.successful) {
              loadPrograms(true);
            } else {
              patchState(store, { errorMessage: result.message || 'Unable to create program.' });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to create program.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('create', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false, deletingProgramId: null });
          });
      },
      update(id: number, program: Program): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'update',
          lastCommandResult: null,
          errorMessage: null,
        });

        void programsApi
          .update(id, program)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('update', result) });
            if (result.successful) {
              loadPrograms(true);
            } else {
              patchState(store, { errorMessage: result.message || 'Unable to update program.' });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to update program.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('update', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false, deletingProgramId: null });
          });
      },
      delete(id: number): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, { deletingProgramId: id });
        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'delete',
          lastCommandResult: null,
          errorMessage: null,
        });

        void programsApi
          .delete(id)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('delete', result) });
            if (result.successful) {
              loadPrograms(true);
            } else {
              patchState(store, { errorMessage: result.message || 'Unable to delete program.' });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to delete program.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('delete', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false, deletingProgramId: null });
          });
      },
      deleteProgram(program: Program): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, { deletingProgramId: program.programId });
        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'delete',
          lastCommandResult: null,
          errorMessage: null,
        });

        void programsApi
          .delete(program.programId)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('delete', result) });
            if (result.successful) {
              loadPrograms(true);
            } else {
              patchState(store, { errorMessage: result.message || 'Unable to delete program.' });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to delete program.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('delete', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false, deletingProgramId: null });
          });
      },
      listProviderServiceKeys,
      createProviderServiceKey(programId: number, name: string): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'createProviderServiceKey',
          lastCommandResult: null,
          errorMessage: null,
        });

        void programsApi
          .createProviderServiceKey(programId, name)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('createProviderServiceKey', result) });
            if (result.successful) {
              listProviderServiceKeys(programId);
            } else {
              patchState(store, { errorMessage: result.message || 'Unable to create provider service key.' });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to create provider service key.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('createProviderServiceKey', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false, deletingProgramId: null });
          });
      },
      updateProviderServiceKey(programId: number, providerServiceKeyId: number, name: string): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'updateProviderServiceKey',
          lastCommandResult: null,
          errorMessage: null,
        });

        void programsApi
          .updateProviderServiceKey(programId, providerServiceKeyId, name)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('updateProviderServiceKey', result) });
            if (result.successful) {
              listProviderServiceKeys(programId);
            } else {
              patchState(store, { errorMessage: result.message || 'Unable to update provider service key.' });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to update provider service key.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('updateProviderServiceKey', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false, deletingProgramId: null });
          });
      },
      deleteProviderServiceKey(programId: number, providerServiceKeyId: number): void {
        if (store.commandInProgress()) {
          return;
        }

        patchState(store, {
          commandInProgress: true,
          lastCommandName: 'deleteProviderServiceKey',
          lastCommandResult: null,
          errorMessage: null,
        });

        void programsApi
          .deleteProviderServiceKey(programId, providerServiceKeyId)
          .then((result) => {
            patchState(store, { lastCommandResult: commandResult('deleteProviderServiceKey', result) });
            if (result.successful) {
              listProviderServiceKeys(programId);
            } else {
              patchState(store, { errorMessage: result.message || 'Unable to delete provider service key.' });
            }
          })
          .catch((error: unknown) => {
            const message = extractErrorMessage(error, 'Unable to delete provider service key.');
            patchState(store, {
              errorMessage: message,
              lastCommandResult: commandResult('deleteProviderServiceKey', failureResult(message)),
            });
          })
          .finally(() => {
            patchState(store, { commandInProgress: false, deletingProgramId: null });
          });
      },
      clearError(): void {
        patchState(store, { errorMessage: null });
      },
      clearCommandResult(): void {
        patchState(store, { lastCommandName: null, lastCommandResult: null, lastProviderServiceKeyResult: null });
      },
    };
  })
);
